import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, GripVertical, Phone, FileText } from 'lucide-react';

export const CallGuide = () => {
  const { get, post, put, del } = useApi();
  const { isAdmin } = useAuth();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGuide, setEditingGuide] = useState(null);
  const [formData, setFormData] = useState({
    titel: '',
    inhalt: '',
    kategorie: '',
    reihenfolge: 0
  });

  const loadGuides = useCallback(async () => {
    try {
      const data = await get('/call-guides');
      setGuides(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Leitfäden');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    loadGuides();
  }, [loadGuides]);

  const resetForm = () => {
    setFormData({
      titel: '',
      inhalt: '',
      kategorie: '',
      reihenfolge: guides.length
    });
    setEditingGuide(null);
  };

  const openEditDialog = (guide) => {
    setEditingGuide(guide);
    setFormData({
      titel: guide.titel,
      inhalt: guide.inhalt,
      kategorie: guide.kategorie || '',
      reihenfolge: guide.reihenfolge
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGuide) {
        await put(`/call-guides/${editingGuide.id}`, formData);
        toast.success('Leitfaden aktualisiert');
      } else {
        await post('/call-guides', formData);
        toast.success('Leitfaden erstellt');
      }
      setDialogOpen(false);
      resetForm();
      loadGuides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (guideId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Leitfaden löschen möchten?')) return;
    try {
      await del(`/call-guides/${guideId}`);
      toast.success('Leitfaden gelöscht');
      loadGuides();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="call-guide-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Call-Leitfaden</h1>
          <p className="text-gray-500 mt-1">
            Strukturierte Gesprächsvorlagen für Ihre Telefonate
          </p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#E63946] hover:bg-[#D62839]" data-testid="create-guide-button">
                <Plus className="h-4 w-4 mr-2" />
                Neuer Leitfaden
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingGuide ? 'Leitfaden bearbeiten' : 'Neuer Leitfaden'}</DialogTitle>
                <DialogDescription>
                  {editingGuide ? 'Aktualisieren Sie den Leitfaden' : 'Erstellen Sie eine neue Gesprächsvorlage'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="guide-form">
                <div className="space-y-2">
                  <Label htmlFor="titel">Titel *</Label>
                  <Input
                    id="titel"
                    value={formData.titel}
                    onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
                    placeholder="z.B. Erstgespräch, Follow-up, Abschluss..."
                    required
                    data-testid="guide-titel-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="kategorie">Kategorie</Label>
                    <Input
                      id="kategorie"
                      value={formData.kategorie}
                      onChange={(e) => setFormData({ ...formData, kategorie: e.target.value })}
                      placeholder="z.B. Einwandbehandlung, Qualifikation..."
                      data-testid="guide-kategorie-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reihenfolge">Reihenfolge</Label>
                    <Input
                      id="reihenfolge"
                      type="number"
                      value={formData.reihenfolge}
                      onChange={(e) => setFormData({ ...formData, reihenfolge: parseInt(e.target.value) || 0 })}
                      data-testid="guide-reihenfolge-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inhalt">Inhalt *</Label>
                  <textarea
                    id="inhalt"
                    className="w-full min-h-[300px] p-3 border border-gray-200 rounded-md focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none font-mono text-sm"
                    value={formData.inhalt}
                    onChange={(e) => setFormData({ ...formData, inhalt: e.target.value })}
                    placeholder={`Beispiel:

## Begrüßung
"Guten Tag, hier ist [Name] von Kaufmanns Consulting..."

## Bedarfsanalyse
- Wie sind Sie aktuell aufgestellt?
- Welche Herausforderungen haben Sie?

## Angebot
...

## Abschluss
...`}
                    required
                    data-testid="guide-inhalt-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button type="submit" className="bg-[#E63946] hover:bg-[#D62839]" data-testid="guide-submit-button">
                    {editingGuide ? 'Aktualisieren' : 'Erstellen'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {guides.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Phone className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-4">Noch keine Leitfäden vorhanden</p>
            {isAdmin && (
              <p className="text-sm text-gray-400">
                Klicken Sie auf "Neuer Leitfaden", um zu beginnen
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="guides-list">
          {guides.map((guide, index) => (
            <Card key={guide.id} className="overflow-hidden" data-testid={`guide-card-${guide.id}`}>
              <CardHeader className="bg-gray-50 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-gray-400">
                      <GripVertical className="h-5 w-5" />
                      <span className="font-mono text-sm">{index + 1}</span>
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="h-5 w-5 text-[#E63946]" />
                        {guide.titel}
                      </CardTitle>
                      {guide.kategorie && (
                        <span className="text-sm text-gray-500">{guide.kategorie}</span>
                      )}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(guide)}
                        data-testid={`edit-guide-${guide.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(guide.id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`delete-guide-${guide.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-700">
                  {guide.inhalt}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
